import { MaterialCommunityIcons } from "@expo/vector-icons";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { useAccount } from "hooks/useAccount";
import { useAppSelector } from "hooks/useAppSelector";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import React from "react";
import { Platform } from "react-native";
import Account from "screens/Account/Account";
import AccountDetails from "screens/AccountDetails/AccountDetails";
import Activity from "screens/Activity/Activity";
import AddMembership from "screens/AddMembership/AddMembership";
import AddNewPlan from "screens/AddNewPlan/AddNewPlan";
import Barcode from "screens/Barcode/Barcode";
import Dashboard from "screens/Dashboard/Dashboard";
import EditProfile from "screens/EditProfile/EditProfile";
import EventAvailablityDetails from "screens/EventAvailablityDetails/EventAvailablityDetails";
import EventDetail from "screens/EventDetail/EventDetail";
import Guests from "screens/Guests/Guests";
import ManageSubscriptionsScreen from "screens/ManageSubscriptions/ManageSubscriptionsScreen";
import MemberCard from "screens/MemberCard/MemberCard";
import MyEvents from "screens/MyEvents/MyEvents";
import MyGifts from "screens/MyGifts/MyGifts";
import PaymentMethod from "screens/PaymentMethod/PaymentMethod";
import PremiumCodeDetails from "screens/PremiumCodeDetails/PremiumCodeDetails";
import Reserve from "screens/Reserve/Reserve";
import Schedule from "screens/Schedule/Schedule";
import SeatSelection from "screens/SeatSelection/SeatSelection";
import Settings from "screens/Settings/Settings";
import PlanDetail from "screens/SubscriptionDetail/SubscriptionDetail";
import TflSeatSelection from "screens/TflSeatSelection/TflSeatSelection";
import UpdateBilling from "screens/UpdateBilling/UpdateBilling";
import UpgradePlans from "screens/UpgradePlans/UpgradePlans";
import { filterIneligibleSubscriptions } from "utils/subscriptionUtils";
import {
  AccountDetailsStackParamList,
  AccountStackParamList,
  DashboardStackParamList,
  ManageSubscriptionsStackParamList,
  MyEventsStackParamList,
  ScheduleStackParamList,
  SettingStackParamList,
} from "./types";

export const DashboardStack =
  createNativeStackNavigator<DashboardStackParamList>();
export const MyEventsStack =
  createNativeStackNavigator<MyEventsStackParamList>();
export const ScheduleStack =
  createNativeStackNavigator<ScheduleStackParamList>();
export const AccountStack = createNativeStackNavigator<AccountStackParamList>();
const AccountDetailsStack =
  createNativeStackNavigator<AccountDetailsStackParamList>();
const ManageSubscriptionsStack =
  createNativeStackNavigator<ManageSubscriptionsStackParamList>();

const SettingsStack = createNativeStackNavigator<SettingStackParamList>();

export function DashboardComponent() {
  return (
    <DashboardStack.Navigator screenOptions={{ headerShown: false }}>
      <DashboardStack.Screen
        name="DashboardScreen"
        options={{ title: "Home" }}
        component={Dashboard}
      ></DashboardStack.Screen>
    </DashboardStack.Navigator>
  );
}

export function MyEventsComponent() {
  return (
    <MyEventsStack.Navigator screenOptions={{ headerShown: false }}>
      <MyEventsStack.Screen
        name="Events"
        component={MyEvents}
        options={{ title: "My games" }}
      ></MyEventsStack.Screen>
      <MyEventsStack.Screen
        name="EventDetail"
        component={EventDetail}
        options={{ title: "Event detail" }}
      ></MyEventsStack.Screen>
      <MyEventsStack.Screen
        name="Barcode"
        component={Barcode}
        options={{ title: "Barcodes" }}
      ></MyEventsStack.Screen>
      <MyEventsStack.Screen
        name="Guests"
        component={Guests}
        options={{ title: "Guests" }}
      ></MyEventsStack.Screen>
    </MyEventsStack.Navigator>
  );
}

export function ScheduleComponent() {
  const { selectedEvent } = useAppSelector((state) => state.reservationFlow);

  return (
    <ScheduleStack.Navigator screenOptions={{ headerShown: false }}>
      <ScheduleStack.Screen
        name="ScheduleScreen"
        component={Schedule}
        options={{ title: "Schedule" }}
      ></ScheduleStack.Screen>
      <ScheduleStack.Screen
        name="EventAvailablityDetails"
        component={EventAvailablityDetails}
        options={{ title: "Event Details" }}
      ></ScheduleStack.Screen>
      {selectedEvent && (
        <>
          <ScheduleStack.Screen
            name="PremiumCodeDetails"
            component={PremiumCodeDetails}
            options={{ title: "Premium Inventory Details" }}
          ></ScheduleStack.Screen>
          <ScheduleStack.Screen
            name="SeatSelection"
            component={SeatSelection}
            options={{ title: "Seats" }}
          ></ScheduleStack.Screen>
          <ScheduleStack.Screen
            name="TflSeatSelection"
            component={TflSeatSelection}
            options={{ title: "Seats" }}
          ></ScheduleStack.Screen>
          <ScheduleStack.Screen
            name="Reserve"
            component={Reserve}
            options={{ title: "Reserve" }}
          ></ScheduleStack.Screen>
        </>
      )}
    </ScheduleStack.Navigator>
  );
}

function AccountDetailsComponent() {
  return (
    <AccountDetailsStack.Navigator screenOptions={{ headerShown: false }}>
      <AccountDetailsStack.Screen
        name="AccountDetailsScreen"
        component={AccountDetails}
        options={{ title: "Account details" }}
      ></AccountDetailsStack.Screen>
    </AccountDetailsStack.Navigator>
  );
}

function ManageSubscriptionsStackComponent() {
  return (
    <ManageSubscriptionsStack.Navigator screenOptions={{ headerShown: false }}>
      <ManageSubscriptionsStack.Screen
        name="ManageSubscriptionsScreen"
        component={ManageSubscriptionsScreen}
        options={{ title: "Manage subscriptions" }}
      ></ManageSubscriptionsStack.Screen>
      <ManageSubscriptionsStack.Screen
        name="SubscriptionDetail"
        component={PlanDetail}
        options={{ title: "Subscription detail" }}
      ></ManageSubscriptionsStack.Screen>
      <ManageSubscriptionsStack.Screen
        name="AddNewPlan"
        component={AddNewPlan}
        options={{ title: "Add New Plan" }}
      ></ManageSubscriptionsStack.Screen>
      <ManageSubscriptionsStack.Screen
        name="AddNewPlanWithPlanPackageId"
        component={AddNewPlan}
        options={{ title: "Add New Plan" }}
      ></ManageSubscriptionsStack.Screen>
    </ManageSubscriptionsStack.Navigator>
  );
}

function SettingComponent() {
  return (
    <SettingsStack.Navigator screenOptions={{ headerShown: false }}>
      <SettingsStack.Screen
        component={Settings}
        name="SettingScreen"
        options={{ title: "Settings" }}
      />
    </SettingsStack.Navigator>
  );
}

export function AccountComponent() {
  const { account } = useAccount();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const isFullAccount =
    account?.type === "FULL" ||
    account?.subscriptions.some(
      (sub) => filterIneligibleSubscriptions(sub) && sub.status === "ACTIVE"
    );

  return (
    <AccountStack.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName="AccountScreen"
    >
      <AccountStack.Screen
        name="AccountScreen"
        component={Account}
        options={{ title: "Account" }}
      />
      <AccountStack.Screen
        name="PaymentMethod"
        component={PaymentMethod}
        options={{ title: "Payment method" }}
      />
      <AccountStack.Screen
        name="UpdateBillingInterval"
        component={UpdateBilling}
        options={{ title: "Update billing" }}
      />
      <AccountStack.Screen
        name="AccountDetails"
        component={AccountDetailsComponent}
        options={{ title: "Account details" }}
      />
      {isFullAccount && (
        <AccountStack.Screen
          name="MemberCard"
          component={MemberCard}
          options={{ title: "Member card" }}
        />
      )}
      <AccountStack.Screen
        name="EditProfile"
        component={EditProfile}
        options={{ title: "Edit profile" }}
      />
      <AccountStack.Screen name="Activity" component={Activity} />
      <AccountStack.Screen name="MyGifts" component={MyGifts} />

      {tenantConfig !== undefined && (
        <>
          {tenantConfig.disableAccountFunctions === false && (
            <>
              <AccountStack.Screen
                name="ManageSubscriptions"
                component={ManageSubscriptionsStackComponent}
                options={{ title: "Manage Subscriptions" }}
              />
              {isFullAccount && (
                <>
                  <AccountStack.Screen
                    name="AddMembership"
                    component={AddMembership}
                    options={{ title: "Add membership" }}
                  />
                  <AccountStack.Screen
                    name="UpgradePlans"
                    component={UpgradePlans}
                    options={{ title: "Upgrade Plans" }}
                  />
                </>
              )}
            </>
          )}
        </>
      )}
      <AccountStack.Screen name="Settings" component={SettingComponent} />
    </AccountStack.Navigator>
  );
}
type IconProps = {
  focused: boolean;
  color: string;
  size: number;
};

export const HomeIcon = ({ color, size }: IconProps) => (
  <MaterialCommunityIcons name="home-outline" size={size} color={color} />
);

export const EventsIcon = ({ color, size }: IconProps) => (
  <MaterialCommunityIcons
    name="stadium-variant"
    color={color}
    size={size}
  ></MaterialCommunityIcons>
);

export const ScheduleIcon = ({ color, size }: IconProps) => (
  <MaterialCommunityIcons
    name="calendar"
    color={color}
    size={size}
  ></MaterialCommunityIcons>
);

export const AccountIcon = ({ color, size }: IconProps) => (
  <MaterialCommunityIcons
    name="account-circle-outline"
    color={color}
    size={size}
  />
);

export const fontFamily = Platform.OS === "web" ? "Roboto-Medium" : undefined;
